import { MapCompositeType } from "@gettactic/api";
import { Pencil, Trash2 } from "lucide-react";
import React from "react";
import { getUniqueId } from "../../../../../../../helpers";
import { CompositeTemplate, Shape, SpotShape } from "../../../../../types";
import { EditorField } from "../../../../ui/EditorField";
import { IconButton } from "../../../../ui/IconButton";
import { PanelSection } from "../../../../ui/PanelShell";
import {
  useMapCanvas,
  useMapCanvasShapes
} from "../../../contexts/MapCanvasContext";
import {
  useMapEditorActions,
  useMapEditorSelection
} from "../../../contexts/MapEditorContext";

/**
 * Parse a MapCompositeType's `data` field into a CompositeTemplate.
 * Returns null if the data doesn't match the expected shape.
 */
function parseApiCompositeData(ct: MapCompositeType): CompositeTemplate | null {
  const d = ct.data as Record<string, unknown> | null;
  if (!d || !Array.isArray(d.shapes) || typeof d.anchorIndex !== "number") {
    return null;
  }
  return {
    id: ct.id,
    name: ct.name,
    shapes: d.shapes as Shape[],
    anchorIndex: d.anchorIndex as number,
    bounds: (d.bounds as { width: number; height: number }) ?? {
      width: 0,
      height: 0
    }
  };
}

export const CompositeTemplatePanelControls: React.FC<{
  shapes: Shape[];
}> = ({ shapes }) => {
  const {
    compositeTemplates,
    setCompositeTemplates,
    setShapes,
    apiCompositeTypes,
    saveCompositeType,
    updateCompositeType,
    deleteCompositeType
  } = useMapCanvas();
  const { shapes: allShapes } = useMapCanvasShapes();
  const { selectedIds } = useMapEditorSelection();
  const { updateShape } = useMapEditorActions();

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingName, setEditingName] = React.useState("");

  const selectedShapes = React.useMemo(
    () => allShapes.filter((s) => selectedIds.includes(s.id)),
    [allShapes, selectedIds]
  );

  const selectedSpots = selectedShapes.filter(
    (s): s is SpotShape => s.type === "spot"
  );
  const selectedNonSpots = selectedShapes.filter((s) => s.type !== "spot");
  const canSave = selectedSpots.length === 1 && selectedNonSpots.length > 0;

  const buildTemplateFromSelection = (): CompositeTemplate | null => {
    if (!canSave) return null;
    const anchor = selectedSpots[0];

    let minX = 0;
    let minY = 0;
    let maxX = anchor.width;
    let maxY = anchor.height;
    for (const s of selectedShapes) {
      const relX = s.x - anchor.x;
      const relY = s.y - anchor.y;
      minX = Math.min(minX, relX);
      minY = Math.min(minY, relY);
      maxX = Math.max(maxX, relX + s.width);
      maxY = Math.max(maxY, relY + s.height);
    }

    const relativeShapes = selectedShapes.map((s) => ({
      ...s,
      x: s.x - anchor.x,
      y: s.y - anchor.y
    }));
    const anchorIndex = relativeShapes.findIndex((s) => s.id === anchor.id);

    return {
      id: `composite-${getUniqueId()}`,
      name: `Composite ${compositeTemplates.length + 1}`,
      shapes: relativeShapes,
      anchorIndex,
      bounds: { width: maxX - minX, height: maxY - minY }
    };
  };

  const applyTemplateToSpot = (template: CompositeTemplate) => {
    if (selectedSpots.length !== 1) return;
    const anchor = selectedSpots[0];

    const companions: Shape[] = [];
    for (let i = 0; i < template.shapes.length; i++) {
      if (i === template.anchorIndex) continue;
      const tShape = template.shapes[i];
      companions.push({
        ...tShape,
        id: `${tShape.type}-${getUniqueId()}`
      } as Shape);
    }

    updateShape(anchor.id, {
      companions,
      compositeTemplateId: template.id
    } as Partial<SpotShape>);
  };

  const embedCompanionsInSpot = (template: CompositeTemplate) => {
    const anchor = selectedSpots[0];
    const companions = selectedNonSpots.map((s) => ({
      ...s,
      x: s.x - anchor.x,
      y: s.y - anchor.y
    }));

    updateShape(anchor.id, {
      companions,
      compositeTemplateId: template.id
    } as Partial<SpotShape>);

    const nonSpotIds = selectedNonSpots.map((s) => s.id);
    setShapes((prev) => prev.filter((s) => !nonSpotIds.includes(s.id)));
  };

  const handleSaveCompositeTemplate = () => {
    const template = buildTemplateFromSelection();
    if (!template) return;

    setCompositeTemplates((prev) => [...prev, template]);
    embedCompanionsInSpot(template);
  };

  const handleSaveToApi = async () => {
    if (!saveCompositeType || !canSave) return;
    const template = buildTemplateFromSelection();
    if (!template) return;

    const result = await saveCompositeType(template.name, {
      shapes: template.shapes,
      anchorIndex: template.anchorIndex,
      bounds: template.bounds
    });

    if (result) {
      // Use the API-returned id for the template link
      const apiTemplate: CompositeTemplate = {
        ...template,
        id: result.id,
        name: result.name
      };
      embedCompanionsInSpot(apiTemplate);
    }
  };

  const handleApplyCompositeTemplate = (templateId: string) => {
    const template = compositeTemplates.find((t) => t.id === templateId);
    if (!template || selectedSpots.length !== 1) return;
    applyTemplateToSpot(template);
  };

  const handleApplyApiTemplate = (ct: MapCompositeType) => {
    const template = parseApiCompositeData(ct);
    if (!template || selectedSpots.length !== 1) return;
    applyTemplateToSpot(template);
  };

  const handleDeleteCompositeTemplate = (templateId: string) => {
    setCompositeTemplates((prev) => prev.filter((t) => t.id !== templateId));
  };

  const handleDeleteApiTemplate = async (id: string) => {
    if (!deleteCompositeType) return;
    await deleteCompositeType(id);
  };

  const handleStartRename = (ct: MapCompositeType) => {
    setEditingId(ct.id);
    setEditingName(ct.name);
  };

  const handleFinishRename = async () => {
    if (!editingId || !updateCompositeType) return;
    const trimmed = editingName.trim();
    if (trimmed) {
      await updateCompositeType(editingId, trimmed);
    }
    setEditingId(null);
    setEditingName("");
  };

  const apiTemplates = apiCompositeTypes ?? [];
  const hasLocalTemplates = compositeTemplates.length > 0;
  const hasApiTemplates = apiTemplates.length > 0;
  const hasAny = hasLocalTemplates || hasApiTemplates;

  return (
    <PanelSection label="Composite Templates">
      <div className="flex flex-col gap-2">
        {canSave && (
          <div className="flex flex-col gap-1">
            <button
              type="button"
              className="w-full rounded-md bg-blue-50 px-2 py-1.5 text-blue-700 text-xs hover:bg-blue-100"
              onClick={handleSaveCompositeTemplate}
            >
              Save as Local Template
            </button>
            {saveCompositeType && (
              <button
                type="button"
                className="w-full rounded-md bg-indigo-50 px-2 py-1.5 text-indigo-700 text-xs hover:bg-indigo-100"
                onClick={handleSaveToApi}
              >
                Save as Shared Template
              </button>
            )}
          </div>
        )}

        {hasApiTemplates && (
          <div className="flex flex-col gap-1">
            <div className="font-medium text-[10px] text-neutral-500 uppercase tracking-wide">
              Shared
            </div>
            {apiTemplates.map((ct) => (
              <div
                key={ct.id}
                className="flex items-center justify-between gap-1 rounded-md border border-indigo-200 px-2 py-1"
              >
                {editingId === ct.id ? (
                  <EditorField
                    label="Template name"
                    containerClassName="flex-1"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={handleFinishRename}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleFinishRename();
                      if (e.key === "Escape") {
                        setEditingId(null);
                        setEditingName("");
                      }
                    }}
                    autoFocus
                  />
                ) : (
                  <button
                    type="button"
                    className="flex-1 truncate text-left text-blue-600 text-xs hover:text-blue-700 disabled:opacity-40"
                    onClick={() => handleApplyApiTemplate(ct)}
                    title={`Apply ${ct.name}`}
                    disabled={selectedSpots.length !== 1}
                  >
                    {ct.name}
                  </button>
                )}
                {updateCompositeType && editingId !== ct.id && (
                  <IconButton
                    size="sm"
                    label="Rename template"
                    onClick={() => handleStartRename(ct)}
                  >
                    <Pencil size={12} />
                  </IconButton>
                )}
                {deleteCompositeType && (
                  <IconButton
                    size="sm"
                    label="Delete template"
                    isDestructive
                    onClick={() => handleDeleteApiTemplate(ct.id)}
                  >
                    <Trash2 size={12} />
                  </IconButton>
                )}
              </div>
            ))}
          </div>
        )}

        {hasLocalTemplates && (
          <div className="flex flex-col gap-1">
            {hasApiTemplates && (
              <div className="font-medium text-[10px] text-neutral-500 uppercase tracking-wide">
                Local
              </div>
            )}
            {compositeTemplates.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between gap-1 rounded-md border border-neutral-200 px-2 py-1"
              >
                <button
                  type="button"
                  className="flex-1 truncate text-left text-blue-600 text-xs hover:text-blue-700 disabled:opacity-40"
                  onClick={() => handleApplyCompositeTemplate(template.id)}
                  title={`Apply ${template.name}`}
                  disabled={selectedSpots.length !== 1}
                >
                  {template.name}
                </button>
                <IconButton
                  size="sm"
                  label="Delete template"
                  isDestructive
                  onClick={() => handleDeleteCompositeTemplate(template.id)}
                >
                  <Trash2 size={12} />
                </IconButton>
              </div>
            ))}
          </div>
        )}

        {!canSave && !hasAny && (
          <div className="py-1 text-center text-neutral-400 text-xs">
            Select a spot + other shapes to save a composite template
          </div>
        )}
      </div>
    </PanelSection>
  );
};
